import { useState, useEffect } from "react";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { FormInput, FormLabel } from "./FormFields";
import {
  listApiTokens,
  createApiToken,
  deleteApiToken,
  ApiToken,
} from "../services/apiTokenService";

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function TokenRow({
  token,
  onRevoke,
}: {
  token: ApiToken;
  onRevoke: (id: string) => void;
}) {
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const isExpired = token.expiresAt && new Date(token.expiresAt) < new Date();

  return (
    <div className="flex items-center gap-3 py-3 border-b border-border last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-primary truncate">
            {token.name}
          </span>
          {isExpired && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-error-bg-strong text-error rounded">
              Expired
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-tertiary">
          <span>Created {formatRelativeTime(token.createdAt)}</span>
          <span className="text-border">|</span>
          <span>
            Last used:{" "}
            {token.lastUsedAt ? formatRelativeTime(token.lastUsedAt) : "Never"}
          </span>
        </div>
      </div>

      {confirmRevoke ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-secondary">Revoke?</span>
          <button
            onClick={() => onRevoke(token.id)}
            className="px-2 py-1 text-xs font-medium text-white bg-error rounded hover:bg-error transition-colors"
          >
            Yes
          </button>
          <button
            onClick={() => setConfirmRevoke(false)}
            className="px-2 py-1 text-xs font-medium text-secondary bg-subtle rounded hover:bg-border transition-colors"
          >
            No
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirmRevoke(true)}
          className="p-1.5 text-tertiary hover:text-error hover:bg-error-bg rounded transition-colors"
          title="Revoke token"
        >
          <span className="material-symbols-outlined text-lg">delete</span>
        </button>
      )}
    </div>
  );
}

function CreateTokenModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (token: string, info: ApiToken) => void;
}) {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) return;

    setCreating(true);
    setError(null);

    try {
      const result = await createApiToken(name.trim());
      onCreated(result.token, result.tokenInfo);
      setName("");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create token");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Generate API Token"
      icon="key"
      size="sm"
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || creating}
            variant="primary"
            loading={creating}
          >
            {creating ? "Creating..." : "Generate Token"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <FormLabel>Token Name</FormLabel>
          <FormInput
            value={name}
            onChange={setName}
            placeholder="e.g., Claude Code, Automation"
          />
          <p className="text-xs text-tertiary mt-1">
            A descriptive name to identify this token
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-error">
            <span className="material-symbols-outlined text-base">error</span>
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

function TokenRevealModal({
  open,
  onClose,
  token,
}: {
  open: boolean;
  onClose: () => void;
  token: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const handleCopy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Token Generated"
      icon="check_circle"
      size="md"
      footer={
        <Button onClick={onClose} variant="primary">
          Done
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="p-3 bg-warning-bg border border-warning/25 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="material-symbols-outlined text-warning text-lg">
              warning
            </span>
            <div className="text-sm text-warning">
              <p className="font-medium">Copy this token now!</p>
              <p className="mt-0.5 text-warning">
                This is the only time you'll see it. Store it securely.
              </p>
            </div>
          </div>
        </div>

        <div>
          <FormLabel>Your API Token</FormLabel>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 text-sm font-mono bg-subtle rounded border border-border break-all">
              {showToken ? token : "••••••••••••••••••••••••••••••••"}
            </code>
            <Button
              onClick={() => setShowToken(!showToken)}
              variant="icon"
              icon={showToken ? "visibility_off" : "visibility"}
              title={showToken ? "Hide token" : "Show token"}
              aria-label={showToken ? "Hide token" : "Show token"}
            />
            <Button
              onClick={handleCopy}
              variant="secondary"
              icon={copied ? "check" : "content_copy"}
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
        </div>

        <div className="text-xs text-tertiary">
          <p className="font-medium mb-1">How to use:</p>
          <p>
            Include this token in the{" "}
            <code className="px-1 py-0.5 bg-subtle rounded">x-api-token</code>{" "}
            header when making requests to the API.
          </p>
        </div>
      </div>
    </Modal>
  );
}

export function ApiTokensSection() {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [revealToken, setRevealToken] = useState<string | null>(null);

  const loadTokens = async () => {
    try {
      const data = await listApiTokens();
      setTokens(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tokens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTokens();
  }, []);

  const handleRevoke = async (tokenId: string) => {
    try {
      await deleteApiToken(tokenId);
      setTokens((prev) => prev.filter((t) => t.id !== tokenId));
    } catch (err) {
      console.error("Failed to revoke token:", err);
    }
  };

  const handleCreated = (token: string, info: ApiToken) => {
    setTokens((prev) => [info, ...prev]);
    setRevealToken(token);
  };

  return (
    <section className="bg-white rounded-2xl p-6 border border-border">
      <div className="flex items-start gap-3 mb-4">
        <span className="material-symbols-outlined text-2xl text-accent">
          key
        </span>
        <div className="flex-1">
          <h2 className="text-base font-bold text-primary mb-1">API Tokens</h2>
          <p className="text-sm text-secondary">
            Generate tokens for REST scripts and automation. Tokens can create
            POIs via the API.
          </p>
          <p className="text-sm text-secondary mt-1">
            Claude, ChatGPT, and Claude Code connect via OAuth at{" "}
            <code className="px-1 py-0.5 bg-subtle rounded">
              https://mcp.plotplango.com/mcp
            </code>{" "}
            — no token needed.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-tertiary py-4">Loading tokens...</div>
      ) : error ? (
        <div className="flex items-center gap-2 text-sm text-error py-4">
          <span className="material-symbols-outlined text-base">error</span>
          {error}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Token list */}
          {tokens.length > 0 && (
            <div className="border border-border rounded-lg divide-y divide-border">
              {tokens.map((token) => (
                <div key={token.id} className="px-4">
                  <TokenRow token={token} onRevoke={handleRevoke} />
                </div>
              ))}
            </div>
          )}

          {/* Empty state or generate button */}
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowCreate(true)}
              variant="primary"
              icon="add"
            >
              Generate Token
            </Button>
            {tokens.length === 0 && (
              <span className="text-sm text-tertiary">No tokens yet</span>
            )}
          </div>

          {/* Help text */}
          <div className="bg-subtle rounded-lg p-4 space-y-2">
            <p className="text-xs font-medium text-primary">
              How to use API tokens:
            </p>
            <ol className="text-xs text-secondary space-y-1 pl-4 list-decimal">
              <li>Generate a token above</li>
              <li>
                Use it with the API:
                <code className="block mt-1 p-2 bg-white rounded border border-border text-xs leading-relaxed">
                  curl -X PUT "$API_URL/api/library/pois" \<br />
                  {"  "}-H "x-api-token: $YOUR_TOKEN" \<br />
                  {"  "}-H "Content-Type: application/json" \<br />
                  {"  "}-d '{"{"}\"pois\": [{"{"}\"id\": \"...\", \"name\": \"My
                  POI\"{"}"}]{"}"}'
                </code>
              </li>
            </ol>
          </div>
        </div>
      )}

      {/* Create modal */}
      <CreateTokenModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />

      {/* Reveal modal */}
      <TokenRevealModal
        open={!!revealToken}
        onClose={() => setRevealToken(null)}
        token={revealToken}
      />
    </section>
  );
}
